import {
  EventByIdQuery,
  EventManyAppQuery,
  EventStatus,
  EventWithOccurrences,
  useEventManyAppQuery,
} from "gql/generated";
import { useWidgetDetailsContext } from "../../WidgetDetailsContextProvider";
import { addMonths, format } from "date-fns";
import { EVENT_DATE_FORMAT, getMultilingualString } from "shared/utils";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useLoggedInAs } from "shared/hooks/useLoggedInAs";
import _ from "lodash";
import { useAppContext } from "AppContext";

const ITEM_HEIGHT = 72;
const ITEM_SPACING = 12;

export const useWidgetItemsEvents = () => {
  const { widget } = useWidgetDetailsContext();
  const { dbUser } = useAppContext();
  const loggedInAs = useLoggedInAs();
  const organizationId = loggedInAs?.entityId;

  // Load all of the events for the widget
  const allEvents = useRef<EventManyAppQuery["eventMany"]>([]);
  const {
    data: events,
    loading: eventsLoading,
    error: eventsError,
  } = useEventManyAppQuery({
    variables: {
      organizationId,
      startDate: format(new Date(), EVENT_DATE_FORMAT),
      endDate: format(
        addMonths(new Date(), widget.eventSettings?.lookAheadMonths || 14),
        EVENT_DATE_FORMAT,
      ),
      status: EventStatus.Approved,
      tags: widget.eventSettings?.eventTags || [],
      skipRecurrences: true,
    },
  });

  // Setup filtering, searching, and alphabet grouping
  const [filteredEvents, setFilteredEvents] = useState<
    EventManyAppQuery["eventMany"]
  >([]);

  // When the events are loaded, sort them by the updated date
  useEffect(() => {
    allEvents.current = _.orderBy(
      events?.eventMany || [],
      "updatedDateISO",
      "desc",
    );
    setFilteredEvents(allEvents.current || []);
  }, [events]);

  /**
   * Search and filter the events
   */
  const [searchValue, setSearchValue] = useState("");
  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchVal = e.target.value;
    setSearchValue(searchVal);

    const searchValLower = searchVal.toLowerCase().trim();
    const filtered = allEvents.current.filter((event) =>
      getMultilingualString(event.title)
        .toLowerCase()
        .trim()
        .includes(searchValLower),
    );

    setFilteredEvents(filtered.length > 0 ? filtered : allEvents.current);
  };

  /**
   * Handle the click of the event details button
   * - This will open the event details modal
   * @param event - The event to view details for
   */
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [isEventDetailsModalOpen, setIsEventDetailsModalOpen] = useState(false);
  const handleEventDetailsClick = (event: EventWithOccurrences) => {
    setSelectedEventId(event._id);
    setIsEventDetailsModalOpen(true);
  };

  /**
   * Handle the close of the event details modal
   */
  const handleEventDetailsModalClose = () => {
    setIsEventDetailsModalOpen(false);
    setSelectedEventId("");
  };

  /**
   * Update or add an event
   * - This allows us to skip refetching the events array when an event is modified in the modal
   * @param event
   */
  const updateOrAddEvent = (event: EventByIdQuery["eventById"]) => {
    if (!event) return;

    const events = [...allEvents.current];
    const existingEventIndex = allEvents.current.findIndex(
      (e) => e._id === event._id,
    );
    if (existingEventIndex !== -1) {
      events[existingEventIndex] = event as EventManyAppQuery["eventMany"][0];
    } else {
      events.push(event as EventManyAppQuery["eventMany"][0]);
    }
    allEvents.current = _.orderBy(events, "updatedDateISO", "desc");
    handleSearchValueChange({
      target: { value: searchValue },
    } as ChangeEvent<HTMLInputElement>);
  };

  /**
   * Remove an event from the events array
   * @param eventId
   */
  const removeEvent = (eventId: string) => {
    if (!eventId) return;

    const events = [...allEvents.current];
    const existingEventIndex = allEvents.current.findIndex(
      (e) => e._id === eventId,
    );
    if (existingEventIndex !== -1) {
      events.splice(existingEventIndex, 1);
    }
    allEvents.current = _.orderBy(events, "updatedDateISO", "desc");
    handleSearchValueChange({
      target: { value: searchValue },
    } as ChangeEvent<HTMLInputElement>);
  };

  return {
    eventsLoading,
    eventsError,
    filteredEvents,
    itemHeight: ITEM_HEIGHT,
    itemSpacing: ITEM_SPACING,
    virtualizedHeight:
      filteredEvents.length * ITEM_HEIGHT +
      ITEM_SPACING * (filteredEvents.length - 1),
    numEvents: filteredEvents.length,
    searchValue,
    onSearchValueChange: handleSearchValueChange,
    handleEventDetailsClick,
    isEventDetailsModalOpen,
    selectedEventId,
    handleEventDetailsModalClose,
    dbUser,
    loggedInAs,
    organizationId,
    updateOrAddEvent,
    removeEvent,
  };
};
